import EditFieldModal from "@/modules/common/components/modals/edit-field-modal";
import { TextButton } from "@/modules/common/components/ui/text-button";
import { TOAST_IDS, ToastType } from "@/modules/common/types";
import { showToast } from "@/modules/common/utils";
import { useEditPropertyRentalInfo } from "@/modules/property-tracks/hooks/rental-info/edit-rental-info.hook";
import { Property } from "@/modules/property-tracks/types";
import { editRentalInfoSchema } from "@/modules/property-tracks/utils/rental-info";
import { zodResolver } from "@hookform/resolvers/zod";
import { PencilLine } from "lucide-react";
import { useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import z from "zod";
import CommonBottomSheet from "@/modules/common/components/ui-kit/bottom-sheets/common-bottom-sheet";
import ModalHeader from "@/modules/common/components/ui-kit/modals/modal-header";
import useScreenSize from "@/modules/common/hooks/device-detection.hook";
import { Box, Stack } from "@mui/material";
import ModalFooter from "@/modules/common/components/ui-kit/modals/modal-footer";
import { useReadonlyContext } from "@/modules/common/context/read-only.context";
import EditRentalInfoForm from "./edit-rental-info-form";

type EditRentalInfoFormFields = z.infer<
  ReturnType<typeof editRentalInfoSchema>
>;

export default function EditRentalInfo({ property }: { property: Property }) {
  const { isMobile } = useScreenSize();
  const [modalOpen, setModalOpen] = useState(false);

  const defaultValues = {
    availableFrom: property.propertyRental?.availableFrom ?? undefined,
    rentalPrice: property.propertyRental?.rentalPrice ?? undefined,
    showingFrom: property.propertyRental?.showingFrom ?? undefined,
  };

  const { handleSubmit, reset, control } = useForm<EditRentalInfoFormFields>({
    resolver: zodResolver(editRentalInfoSchema()),
  });

  const onSubmit: SubmitHandler<EditRentalInfoFormFields> = async (data) => {
    submitEditProperty(data);
  };

  const mutation = useEditPropertyRentalInfo();

  const submitEditProperty = (data: EditRentalInfoFormFields) => {
    mutation.mutate(
      {
        uid: property.uid,
        data: data,
      },
      {
        onSuccess: () => {
          setModalOpen(false);
          reset();
          showToast({
            type: ToastType.SUCCESS,
            message: "Property Rental Info updated successfully",
            toastId: TOAST_IDS.EDIT_RENTAL_DETAILS_SUCCESS,
            options: {
              position: "bottom-left",
              autoClose: 3000,
            },
          });
        },
        onError: (error) => {
          showToast({
            type: ToastType.ERROR,
            message:
              error?.response?.data?.message ||
              "Unexpected error occurred. Please try again.",
            toastId: TOAST_IDS.EDIT_RENTAL_DETAILS_ERROR,
            options: {
              position: "bottom-left",
              autoClose: 3000,
            },
          });
        },
      }
    );
  };

  const handleModalOpen = () => {
    setModalOpen(true);
    reset(defaultValues);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    reset();
  };

  const { readonly } = useReadonlyContext();

  return (
    <>
      <TextButton
        disabled={readonly}
        onClick={handleModalOpen}
        sx={{ display: "flex", gap: 0.5, alignItems: "bottom" }}
      >
        <PencilLine size={16} /> Edit
      </TextButton>

      {isMobile ? (
        <CommonBottomSheet
          isOpen={modalOpen}
          onClose={handleCloseModal}
          snapPoints={[0.5, 0.8]}
        >
          <ModalHeader title="Edit Rental Details" onClose={handleCloseModal} />
          <Box>
            <Stack gap={1} p={2}>
              <EditRentalInfoForm control={control} />
            </Stack>
            <Box position="sticky" bottom={0}>
              <ModalFooter
                confirmButtonText="Save"
                cancelButtonText="Cancel"
                onConfirm={handleSubmit(onSubmit)}
                isLoading={mutation.isPending}
                onCancel={handleCloseModal}
              />
            </Box>
          </Box>
        </CommonBottomSheet>
      ) : (
        <EditFieldModal
          open={modalOpen}
          setOpen={setModalOpen}
          header="Edit Rental Details"
          width={480}
          handleConfirm={handleSubmit(onSubmit)}
          handleCloseModal={handleCloseModal}
          isLoading={mutation.isPending}
        >
          <EditRentalInfoForm control={control} />
        </EditFieldModal>
      )}
    </>
  );
}
